import { readAppDisplay } from './client-type.js';

/**
 * ★ **진입 게이트를 세울 것인가** (v26 U10 · 절대 규칙 72).
 *
 * ── 왜 있는가 ────────────────────────────────────────────────
 *
 * **설치된 앱은 TV 에 가까워야 한다** — 노트북 홈 화면의 Chrome 데스크톱 PWA 와 Android
 * 폰의 PWA 앱을 열면 **기억한 채널이 바로** 나온다. **브라우저 탭은 지금 그대로** 게이트를
 * 거친다.
 *
 * ── 갈리는 것은 「묻는가」 하나다 ────────────────────────────
 *
 * **어느 채널을 틀지는 이미 정해져 있다** — `resolveChannel` 이 `bloom.channelId` 를 보고
 * (v9), 홈쇼핑이면 `entryStartFor` 가 「어느 항목부터」까지 준다(v26). 그 사이에 서 있는
 * 것은 `StartGate` 하나이고, **그것이 있는 이유는 채널 판정이 아니라 소리 있는 재생 허가
 * 한 번**이다 (`playback-buffers.ts` 머리 · `docs/06` 「진입 흐름」).
 *
 * ── ★★ 정책을 직접 물을 수 없다 (2026-09-02 실기) ────────────
 *
 * 이 판정의 첫 안은 **`navigator.getAutoplayPolicy('mediaelement')`** 였다. 브라우저가
 * 스스로 답하는 값이라 가장 정직하고, 「설치되었는가」와 AND 로 묶으면 표면마다 옳았다.
 *
 * **그런데 Chrome 에 그 함수가 없다.** 하네스를 처음 돌린 창에서 `'no-api'` 였고
 * (`--autoplay-policy` 플래그를 켠 채였다), 그 API 는 **Firefox 120+ 에만** 있다.
 * 그대로 뒀으면 **노트북 Chrome PWA 와 Android PWA 에서 영원히 거짓** — 요구한 그 둘에서
 * 기능이 통째로 죽는데, **증상은 「아무 일도 안 일어난다」 하나**다.
 *
 * **그래서 이 저장소가 실측으로 아는 사실 하나로 대신한다** — **WebKit 인가.**
 * v12 가 아이폰에서 만난 것이 정확히 그것이고(제스처 밖 `start()` 가 `PLAYBACK_FAILED`),
 * v19 가 `<video>` 한 쌍을 살려 그 조건을 없앤 것도 같은 사실 위에 서 있다.
 *
 * | | `'standalone' in navigator` | `navigator.standalone === true` | `(display-mode: standalone)` |
 * |---|---|---|---|
 * | 묻는 것 | **iOS 계열인가** (여기) | iOS **홈 화면 앱**인가 (`auth/login-mode.ts`) | **설치된 앱 창인가** (여기 · `fullscreen`·`minimal-ui` 도 함께 본다) |
 * | Chrome·Firefox | 거짓 | 거짓 | 설치했으면 참 |
 * | 사파리 탭·홈 화면 앱·WKWebView | **셋 다 참** | 가운데만 참 | 홈 화면 앱만 참 |
 *
 * **`audio-muted.ts` 가 이미 같은 사실을 같은 방법으로 읽는다** — 값이 아니라 **있고
 * 없음**이다. 값으로 보면 **사파리 탭이 판정에서 빠지는데**, 그쪽은 `standalone` 이 거짓이라
 * 결과는 같지만 **뜻이 달라진다**(그때 이 함수는 「홈 화면 앱이 아니다」를 말하게 된다).
 *
 * ── ★ 그래서 자물쇠 ②가 곁가지가 아니다 ─────────────────────
 *
 * 「WebKit 인가」는 **정책의 대리(proxy)이지 정책이 아니다.** macOS 사파리의 Dock 앱처럼
 * 우리가 모르는 표면이 있고, 브라우저가 판단을 바꿀 수도 있다. 그래서 **거부됐을 때
 * 오류가 아니라 게이트로 되돌리는 것**(`isAutoplayRejection` · `useReceiver`)이 이 설계의
 * 두 번째 절반이다. 물음이 틀려도 시청자가 보는 것은 **버튼 하나**여야 한다.
 *
 * ── `CLAUDE.md` 의 그 금지와 부딪히지 않는다 ─────────────────
 *
 * 「`(display-mode: standalone)` 으로 **플랫폼**을 가르지 말 것」이 막는 것은 그 값으로
 * 「그 화면에서 팝업이 돌아오는가」를 묻는 오용이고, **그 문장 자체가 「그것은 『설치되었는가』
 * 이다」라고 적는다.** 여기서 묻는 것이 정확히 그 「설치되었는가」이고, **혼자 서지도
 * 않는다.** 같은 축을 쓰는 자리가 이미 하나 있다 — `usesKeyboardNav`(v19).
 *
 * **`clientType` 은 건드리지 않는다** (절대 규칙 29 의 그 경고) — 설치된 앱도 `pc_web`·
 * `mobile_web` 그대로다. 바꾸면 구매 문구가 「모바일에서 구매」로 가고 이력 승격이 꺼진다.
 */
export interface AutoStartFacts {
  /**
   * 설치된 앱 창인가 — `readAppDisplay().installed`.
   *
   * ★ **`display-mode: standalone` 하나다** (v26 U11 은 `fullscreen` 을 더했다가
   * **되돌렸다** — `AppDisplay.installed` 의 표). 전체화면인 브라우저 창과 값이 같아
   * 게이트가 통째로 사라졌고, 그래서 여기서는 **모르는 쪽이 게이트다**(절대 규칙 72).
   *
   * **`readAppDisplay().fullscreen` 을 여기로 들이지 말 것.** 그 칸은 키 조작
   * (`usesKeyboardNav`)만 읽는다 — 같은 모호함에서 대가가 반대 방향이라 판단도 반대다.
   */
  installed: boolean;
  /**
   * iOS 계열인가 — `navigator.standalone` 의 **유무**가 그 판정이다.
   * **값이 아니다** (`audio-muted.ts` 의 그 표).
   */
  iosFamily: boolean;
}

/**
 * 규칙. **사실을 읽는 자리와 가른다** — `audio-muted.ts` 의 `volumeIsControllable` 과
 * `auth/login-mode.ts` 가 같은 이유로 같은 모양이다: 붙어 있으면 **규칙이 틀려도 테스트가
 * 볼 것이 없다.**
 */
export function autoStartsWithoutGesture(facts: AutoStartFacts): boolean {
  return facts.installed && !facts.iosFamily;
}

/**
 * ★ **자동재생 정책이 거부한 것인가** (자물쇠 ② · 절대 규칙 72).
 *
 * 어댑터가 `await el.play()` 를 그대로 던지고(`hls-player.ts`·`native-hls-player.ts` 둘 다
 * 감싸지 않는다) 코어가 `fail({ code:'PLAYBACK_FAILED', cause: e })` 로 실어 올린다.
 * 그래서 **원인이 여기까지 온전히 온다.**
 *
 * **`instanceof DOMException` 으로 보지 않는다** — 이 패키지의 테스트는 node 환경이고
 * (jsdom 미도입), 그 전역이 있고 없고에 판정이 매달리면 **규칙이 검사 밖으로 나간다.**
 * 이름 하나만 본다.
 */
export function isAutoplayRejection(cause: unknown): boolean {
  if (typeof cause !== 'object' || cause === null) return false;
  return (cause as { name?: unknown }).name === 'NotAllowedError';
}

/**
 * 사실을 읽는다. **DOM 과 `navigator` 를 읽는 자리는 여기 하나다.**
 */
export function readAutoStartFacts(): AutoStartFacts {
  const { installed } = readAppDisplay();
  return {
    installed,
    iosFamily: typeof navigator !== 'undefined' && 'standalone' in navigator,
  };
}

/**
 * 판정의 결과. 화면에는 **게이트가 뜨는가 하나로만** 드러난다.
 *
 * - `installed` — 묻지 않고 시작한다
 * - `gate` — 묻는다 (브라우저 탭 · 아이폰 홈 화면 앱)
 * - `refused` — **묻지 않고 시작했는데 거부됐다** (자물쇠 ②가 걸린 자리)
 */
export type AutoStartDecision = 'installed' | 'gate' | 'refused';

/**
 * ★ **판정을 문서 루트에 적는다** (`use-tv-focus.ts` 의 `data-tv-focus` 와 같은 방침).
 *
 * **이 기능의 실패는 조용하다** — 판정이 거짓이면 **아무 일도 일어나지 않고 게이트가
 * 그대로 뜬다.** 그것은 화면에서 **「구현이 안 된 것」과 완전히 같다.** 위 ★★ 가 실제로
 * 그 모양이었다.
 *
 * 그래서 하네스가 신고할 수 있고 무대에서 개발자 도구 한 줄로 이유가 나오는 축을 남긴다
 * (`?debug=1` 이 필요 없다 — **설치된 앱에는 주소창이 없다**).
 *
 * `facts` 를 주면 사실도 함께 적는다. 되돌림(`refused`)은 그때의 사실을 다시 읽을 필요가
 * 없으므로 주지 않는다.
 */
export function markAutoStart(decision: AutoStartDecision, facts?: AutoStartFacts): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.autoStart = decision;
  if (facts !== undefined) {
    root.dataset.autoStartFacts = `installed=${String(facts.installed)},ios=${String(facts.iosFamily)}`;
  }
}

/**
 * 읽고·판정하고·적는다. **화면이 부르는 것은 이것 하나다.**
 *
 * 게이트가 이미 필요 없는 경우(`buffersUnlocked()` — 같은 문서에서 이미 소리 있는 재생이
 * 붙었다)는 **여기서 보지 않는다.** 그것은 「설치되었는가」와 무관한 별개의 근거이고,
 * 섞으면 `data-auto-start` 가 두 가지 뜻을 갖는다.
 */
export function decideAutoStart(): boolean {
  const facts = readAutoStartFacts();
  const decided = autoStartsWithoutGesture(facts);
  markAutoStart(decided ? 'installed' : 'gate', facts);
  return decided;
}

/**
 * ★★ **진입 게이트를 그릴 것인가** (2026-09-06 무대 요청 — 「채널 이동 사이에 시작
 * 게이트가 잠깐 보이는 것을 원천적으로 없애라」).
 *
 * ── 무엇이 바뀌었나 ─────────────────────────────────────────
 *
 * 지금까지 화면 둘이 **`!view.started`** 로 그렸다. 그런데 그 상태는 **마운트마다 거짓에서
 * 시작**하고(`useReceiver` 의 `useState(false)`), 채널 전환은 규칙 27 때문에 **반드시
 * 마운트를 거친다** — 그래서 첫 페인트에 게이트가 한 프레임 그려졌다. 실측 **2~12ms**
 * 였고, ONAIR 는 고치기 전 **102~131ms** 였다.
 *
 * **그 창은 타이밍이 아니라 구조였다.** `useLayoutEffect` 로 옮기면 짧아질 뿐 없어지지
 * 않고, 느린 기기에서 얼마가 될지는 아무도 모른다.
 *
 * ── 그래서 조건을 바꾼다 ────────────────────────────────────
 *
 * 게이트는 **자동재생 정책 때문에** 있다(절대 규칙 72). 그러면 조건도 정책 쪽 사실이어야
 * 하는데 「아직 안 틀었다」는 다른 사실이다 — 둘이 겹치는 것은 **첫 진입뿐**이라 지금까지
 * 지나갔다.
 *
 * ```
 *   틀었다            → 없다
 *   거부당했다        → 있다      ← 규칙 72 의 둘째 자물쇠
 *   그 밖            → 이 문서에서 아직 허가가 없고, 자동 시작도 아닐 때만
 * ```
 *
 * ★ **`unlocked` 가 모듈 스코프라는 것이 이 판정의 전부다**(`playback-buffers.ts`) —
 * 재마운트를 건너 살아남으므로 **전환에서는 조건이 원리적으로 성립할 수 없다.**
 * 첫 페인트가 아무리 늦어도 게이트가 그려지지 않는다.
 *
 * ── 게이트가 사라진 자리는 누가 갖는가 ──────────────────────
 *
 * **잡아 둔 마지막 프레임**이다(`freeze-frame.ts`). 그것 없이 이 판정만 바꾸면 검은
 * 화면이 드러나므로 **순서가 있다** — v28 의 그 기능이 먼저다.
 * 그리고 진짜 실패는 여전히 오류 스크림이 갖는다(`EMPTY_PLAYLIST` 도 그리로 간다).
 */
export function showsStartGate(input: {
  /** 재생이 한 번이라도 붙었는가 */
  started: boolean;
  /** 이 문서에서 소리 있는 재생이 성립한 적이 있는가 (`buffersUnlocked()`) */
  unlocked: boolean;
  /** 전환·알림처럼 **제스처 없이 시작하는 경로**인가 */
  autoStart: boolean;
  /** 자동재생이 거부돼 게이트로 되돌아왔는가 (규칙 72 의 자물쇠 ②) */
  gestureRequired: boolean;
}): boolean {
  if (input.started) return false;
  // **거부가 이깁니다.** 설치된 앱은 `autoStart` 가 참인 채로 여기 오므로, 이 줄이
  // 없으면 되돌아올 자리가 없어진다 — 아이폰 홈 화면 앱이 그 경로다.
  if (input.gestureRequired) return true;
  return !input.unlocked && !input.autoStart;
}
